import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/reducers';
import { AuthService } from 'src/app/core/auth';
import { Subscription } from 'rxjs';
import {
  HttpUtilsService,
  LayoutUtilsService,
  TypesUtilsService,
} from '../../../../../core/_base/crud';
import { CommentsService } from 'src/app/core/services/comments.service';
import { FollowUpService } from '../../../../../core/services/follow-up.service';
import { QuickCommentsService } from '../../../../../core/services/quick-comments.service';
import { UserService } from '../../../../../core/auth/_services';
import { take } from 'rxjs/operators';

export enum CommentAction {
  'CREATE' = 'CREATE',
  'UPDATE' = 'UPDATE',
  'DELETE' = 'DELETE',
}

@Component({
  selector: 'kt-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
})
export class CommentsComponent implements OnInit, OnDestroy {
  private _taskTemplateId: number; // This template id used to connect the comments component inside task with quick comments feature.
  @Input()
  get taskTemplateId(): number {
    return this._taskTemplateId;
  }
  set taskTemplateId(value: number) {
    this._taskTemplateId = value;

    if (value)
      this.quickCommentsService
        .getQuickComments(value)
        .pipe(take(1))
        .subscribe((res) => {
          this.quickComments = res?.data || [];
        });
  }
  @Input() id;
  @Input() followup_id;
  @Input() comments;
  @Input() seeAllShow;
  @Input() type;
  @Input() height;
  @Input() readonly = false;
  @Input() fromDialog = true;
  @Output() seeAll = new EventEmitter<any>();
  @Output() postedFirstComment = new EventEmitter<void>();
  @Output() editingEventPlayed = new EventEmitter<boolean>();
  @Output() commentsChange = new EventEmitter<{ action: CommentAction; comment }>();
  quickComments = [];
  displayComments;
  editCommentValue;
  newComment = '';
  currentUser;
  isSubmittingComment: boolean;
  isEditing: boolean;
  subscriptions: Subscription[] = [];
  see_all = false;
  rows = 1;
  inputText = '';
  constructor(
    private httpUtils: HttpUtilsService,
    private layoutUtilsService: LayoutUtilsService,
    private commentsService: CommentsService,
    private followUpService: FollowUpService,
    private cdr: ChangeDetectorRef,
    private typesUtilsService: TypesUtilsService,
    private store: Store<AppState>,
    private auth: AuthService,
    private userService: UserService,
    private quickCommentsService: QuickCommentsService,
  ) {}

  ngOnInit() {
    this.auth.getUserByToken().subscribe((user) => {
      this.currentUser = user;
    });
    this.displayComments = this.comments?.slice(0, 4);

    if (!this.fromDialog && !!this.comments?.length) {
      const usernames = [...new Set(this.comments.map((c) => c.last_modified_user))];

      this.userService
        .getUserByUserName(usernames)
        .pipe(take(1))
        .subscribe((res) => {
          if (res?.pageOfItems?.length) {
            this.comments = this.comments.map((c) => ({
              ...c,
              userName: c.last_modified_user,
              profileUrl: res.pageOfItems.find((i) => i.userName === c.last_modified_user)
                ?.profileUrl,
            }));

            this.displayComments = this.comments.slice(0, 4);
          }
        });
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  postComment(comment) {
    if (comment.length > 0) {
      this.isSubmittingComment = true;
      if (!this.followup_id)
        this.commentsService.postComment(this.type, this.id, comment).subscribe((res) => {
          if (res && res.data) {
            setTimeout(() => {
              if (!this.comments.length) this.postedFirstComment.emit();

              const newComment = res.data;
              newComment.profileUrl = this.currentUser.profileUrl;
              newComment.editMode = false;
              newComment.userName = newComment.last_modified_user;
              this.comments.unshift(newComment);
              this.commentsChange.emit({ action: CommentAction.CREATE, comment: newComment });
              this.isSubmittingComment = false;
              this.cdr.detectChanges();
            }, 2000);
          }
        });
      if (this.followup_id) {
        const body = { comment, transaction_id: this.id };
        this.followUpService
          .postFollowUpComment(this.type, this.followup_id, body)
          .subscribe((res) => {
            if (res) {
              setTimeout(() => {
                const newComment = res;
                newComment.profileUrl = this.currentUser.profileUrl;
                newComment.editMode = false;
                newComment.userName = newComment.last_modified_user;
                this.comments.unshift(newComment);
                this.isSubmittingComment = false;
                this.cdr.detectChanges();
              }, 2000);
            }
          });
      }
    }
  }

  updateComment(comment) {
    if (this.editCommentValue.trim().length > 0) {
      this.isSubmittingComment = true;
      const updatedComment = this.comments.find((c) => c.id === comment.id);
      if (!this.followup_id)
        this.commentsService
          .updateComment(this.type, this.id, comment.id, this.editCommentValue.trim())
          .subscribe((res) => {
            if (res && res.data) {
              updatedComment.comment = res.data.comment;
              this.commentsChange.emit({ action: CommentAction.UPDATE, comment: updatedComment });
            }
            this.isEditing = false;
            this.cdr.detectChanges();
          });
      if (this.followup_id) {
        const body = { comment: this.editCommentValue.trim(), transaction_id: this.id };
        if (!this.see_all)
          this.followUpService
            .updateFollowupComment(
              this.type,
              comment.transaction_comment_id ? comment.transaction_comment_id : '',
              comment.id,
              this.followup_id,
              body,
            )
            .subscribe((res) => {
              if (res) {
                updatedComment.comment = res.comment;
              }
              this.isEditing = false;
              this.cdr.detectChanges();
            });
        if (this.see_all)
          this.followUpService
            .updateFollowupComment(
              this.type,
              comment.id ? comment.id : '',
              '',
              this.followup_id,
              body,
            )
            .subscribe((res) => {
              if (res) {
                updatedComment.comment = res.comment;
              }
            });
        this.isEditing = false;
        this.cdr.detectChanges();
      }
      updatedComment.editMode = false;
      this.isSubmittingComment = false;
    }
  }

  deleteComment(comment) {
    const _title = 'Comment Delete';
    const _description = 'Are you sure you want to permanently delete this comment?';
    const _waitDesciption = 'Comment is deleting...';

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res1) => {
      if (!res1) {
        return;
      }
      if (!this.followup_id)
        this.commentsService.deleteComment(this.type, this.id, comment.id).subscribe((res) => {
          if (res?.ok) {
            this.comments.splice(
              this.comments.findIndex((obj) => obj.id === comment.id),
              1,
            );
            this.commentsChange.emit({ action: CommentAction.DELETE, comment });
            this.cdr.detectChanges();
          }
        });
      if (this.followup_id) {
        if (!this.see_all)
          this.followUpService
            .deleteFollowUpComment(
              this.type,
              comment.transaction_comment_id ? comment.transaction_comment_id : '',
              comment.id,
              this.followup_id,
              this.id,
            )
            .subscribe((res) => {
              if (res === null) {
                this.comments.splice(
                  this.comments.findIndex((obj) => obj.id === comment.id),
                  1,
                );
                this.cdr.detectChanges();
              }
            });
        if (this.see_all)
          this.followUpService
            .deleteFollowUpComment(
              this.type,
              comment.id ? comment.id : '',
              '',
              this.followup_id,
              this.id,
            )
            .subscribe((res) => {
              if (res === null) {
                this.comments.splice(
                  this.comments.findIndex((obj) => obj.id === comment.id),
                  1,
                );
                this.cdr.detectChanges();
              }
            });
      }
    });
  }

  symbolCSS(name) {
    const color = this.typesUtilsService.getRandomColorBasedString(name);
    return { 'background-color': color ? color : '#E1F0FF', color: color ? 'white' : '#8950FC' };
  }

  onScroll(event) {
    if (
      event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight &&
      this.displayComments.length < this.comments.length
    ) {
      this.displayComments.push(this.comments[this.displayComments.length]);
    }
  }

  sortByDate(comments) {
    return this.httpUtils.sortArray(comments, 'updated', 'desc');
  }
  seeAllComments() {
    this.see_all = !this.see_all;
    this.seeAll.emit(this.see_all);
  }
  getSeeWord() {
    if (this.see_all) return 'See less';
    return 'See all';
  }
  pressKey(event, c) {
    if (event.keyCode === 13 && !event.shiftKey) {
      this.updateComment(c);
      event.preventDefault();
      this.editingEventPlayed.emit(false);
    }
  }
  pressKeyPost(event) {
    if (event.keyCode === 13 && !event.shiftKey) {
      this.postComment(this.newComment);
      this.newComment = '';
      document.getElementById('newCommentTextarea').blur();
      this.editingEventPlayed.emit(false);
    }
  }
  onCommentPosted() {
    if (this.quickComments?.length === 0) return;
    this.newComment = this.newComment.replace(/\*\[(.*?)\]/g, (match) => {
      const code = match.substring(2, match.length - 1);
      return this.quickComments.find((item) => item.code === code)?.comment || match;
    });
  }
  onCommentUpdated() {
    if (this.quickComments?.length === 0) return;
    this.editCommentValue = this.editCommentValue.replace(/\*\[(.*?)\]/g, (match) => {
      const code = match.substring(2, match.length - 1);
      return this.quickComments.find((item) => item.code === code)?.comment || match;
    });
  }
  addNewCode(comment) {
    this.newComment = this.newComment + ' ' + comment.comment + ' ';
  }
  addCodeToUpdate(comment) {
    this.editCommentValue = this.editCommentValue + ' ' + comment.comment + ' ';
  }
}
